import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Profile } from '@/api/admin/profiles';
import type { ControlNode } from '@/lib/controls-definition';
import { getAncestorCodes, getDescendantCodes } from '@/lib/controls-utils';

interface UseLockedControlDraftReturn {
  /** Current local state of enabled codes */
  draftCodes: Set<string>;
  /** Toggle a code with cascade logic (enable ancestors, disable descendants) */
  toggleCode: (code: string, node: ControlNode) => void;
  /** Diff between draft and server state */
  computeDiff: () => { toAdd: string[]; toRemove: string[] };
  /** Whether draft differs from server */
  isDirty: boolean;
  /** Discard local changes, re-sync from server */
  reset: () => void;
}

/**
 * Hook that clones `profile.locked_control_code` into local `Set<string>` state
 * and manages toggle changes locally with cascade logic.
 *
 * - **Enable:** adds the code + all ancestor codes
 * - **Disable:** removes the code + all descendant codes, then cleans orphaned ancestors
 */
export function useLockedControlDraft(
  profile: Profile | null | undefined,
): UseLockedControlDraftReturn {
  const serverCodes = useMemo(
    () => new Set(profile?.locked_control_code ?? []),
    [profile?.locked_control_code],
  );

  const [draftCodes, setDraftCodes] = useState<Set<string>>(() => new Set(serverCodes));

  // Track profile.id to re-initialize when it changes
  const lastProfileId = useRef(profile?.id);

  useEffect(() => {
    if (profile?.id !== lastProfileId.current) {
      lastProfileId.current = profile?.id;
      setDraftCodes(new Set(profile?.locked_control_code ?? []));
    }
  }, [profile?.id, profile?.locked_control_code]);

  const toggleCode = useCallback((code: string, _node: ControlNode) => {
    setDraftCodes((prev) => {
      const next = new Set(prev);
      const isEnabled = next.has(code);

      if (!isEnabled) {
        // Enable: add the code + all ancestor codes
        next.add(code);
        for (const ancestor of getAncestorCodes(code)) {
          next.add(ancestor);
        }
      } else {
        // Disable: remove the code + all descendant codes
        next.delete(code);
        for (const descendant of getDescendantCodes(code)) {
          next.delete(descendant);
        }

        // Clean orphaned ancestors: walk up from the code and remove
        // parent codes that no longer have any enabled children in the draft
        const parts = code.split(':');
        for (let i = parts.length - 1; i >= 1; i--) {
          const parentCode = parts.slice(0, i).join(':');
          const parentPrefix = `${parentCode}:`;

          // Check if any sibling/child under this parent is still enabled
          let hasEnabledChild = false;
          for (const c of next) {
            if (c.startsWith(parentPrefix)) {
              hasEnabledChild = true;
              break;
            }
          }

          if (!hasEnabledChild) {
            next.delete(parentCode);
          } else {
            // If a parent still has children, ancestors above it are not orphaned
            break;
          }
        }
      }

      return next;
    });
  }, []);

  const computeDiff = useCallback(() => {
    const toAdd: string[] = [];
    const toRemove: string[] = [];

    for (const code of draftCodes) {
      if (!serverCodes.has(code)) {
        toAdd.push(code);
      }
    }

    for (const code of serverCodes) {
      if (!draftCodes.has(code)) {
        toRemove.push(code);
      }
    }

    return { toAdd, toRemove };
  }, [draftCodes, serverCodes]);

  const isDirty = useMemo(() => {
    if (draftCodes.size !== serverCodes.size) return true;
    for (const code of draftCodes) {
      if (!serverCodes.has(code)) return true;
    }
    return false;
  }, [draftCodes, serverCodes]);

  const reset = useCallback(() => {
    setDraftCodes(new Set(profile?.locked_control_code ?? []));
  }, [profile?.locked_control_code]);

  return {
    draftCodes,
    toggleCode,
    computeDiff,
    isDirty,
    reset,
  };
}
